import { DecimalFormat } from "./DecimalFormat";
import { StringUtil } from "./StringUtil";

export class NumberUtil {
    public static removeFormatting(value: string): string {
        return value.replace(/,/g, '');
    }

    public static formatInteger(value: number, format?: string): string {
        if (value != null && !isNaN(value)) {
            if (format == null)
                return new Intl.NumberFormat("en-US", { useGrouping: false }).format(value);
            else
                return new DecimalFormat(format).format(value);
        }
        return null;
    }

    public static formatDecimal(value: number, format?: string): string {
        if (value != null && !isNaN(value)) {
            if (format == null) {
                return new Intl.NumberFormat("en-US", {
                    style: "decimal",
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 20  // Overrides the default 3 digit limit to prevent default rounding behavior
                }).format(value);
            }
            else
                return new DecimalFormat(format).format(value);
        }
        return null;
    }

    public static areEqual(value1: any, value2: any): boolean {
        if (value1 == value2)
            return true;
        else if (value1 == null || value2 == null)
            return false;
        else
            return NumberUtil.getFloat(value1) == NumberUtil.getFloat(value2);
    }

    public static getFloat(value: any): number {
        if (StringUtil.isEmptyString(value))
            return null;
        return parseFloat(this.removeFormatting(value.toString()));
    }

    /**
     * Mimics Java's Integer.parseInt by ensuring the entire string is a valid integer.
     * Unlike JavaScript's parseInt, this does NOT allow partial parsing (e.g., "2abc" returns an error).
     *
     * @param value The string to parse.
     * @returns The parsed integer.
     * @throws Error if parsing fails (invalid input, non-integer, out of range).
     */
    public static parseIntStrict(value: string | number): number {
        if (typeof value === "number") {
            if (Number.isInteger(value)) return value;
            throw new Error(`Invalid integer: ${value}`);
        }

        if (typeof value !== "string" || StringUtil.isEmptyString(value)) {
            throw new Error(`Invalid integer: ${value}`);
        }

        value = value.trim();

        // Ensure value contains only valid integer characters (no commas, decimals, or letters)
        if (!/^[-+]?\d+$/.test(value)) {
            throw new Error(`Invalid integer format: ${value}`);
        }

        const parsedValue = Number(value);
        if (!Number.isSafeInteger(parsedValue)) {
            throw new Error(`Integer out of range: ${value}`);
        }
        return parsedValue;
    }
}
